/**
 * Materials close-out on the crew iPad — `purchasing-close-out.png`.
 *
 * This is the moment cost hits the job, and the crew is the only one who knows
 * the numbers. So the screen says so plainly, and makes the arithmetic visible
 * while they type: every line shows accounted against loaded, and a line that
 * does not add up is amber before they press anything rather than after.
 *
 * Used and wasted leave the yard. Returned does not — it never left the
 * yard's stock (§A.1), and the only reason it is on this screen is so the
 * three numbers add up to what went out on the truck.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { Button, Card, EmptyState, SectionLabel, Stepper, Tag } from '../components/primitives.js'
import { Modal, Toast } from '../components/overlays.js'
import { Field } from '../components/fields.js'
import { formatCents, formatNumber } from '../lib/format.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import type { CloseoutLine, CloseoutSheet } from '../lib/purchasing-types.js'

interface Draft extends CloseoutLine {
  dirty: boolean
}

export function CrewCloseout({ me }: { me: MeResponse }) {
  const navigate = useNavigate()
  const { visitId = '' } = useParams()

  const [sheet, setSheet] = useState<CloseoutSheet | null>(null)
  const [lines, setLines] = useState<Draft[]>([])
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [receipt, setReceipt] = useState<null | {
    vendorName: string; amount: string; description: string
  }>(null)

  const load = useCallback(async () => {
    try {
      const payload = await request<CloseoutSheet>(`/purchasing/visits/${visitId}/closeout`)
      setSheet(payload)
      setLines(payload.lines.map((line) => ({ ...line, dirty: false })))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That close-out could not load.')
    }
  }, [visitId])

  useEffect(() => { void load() }, [load])

  function update(itemId: string, field: 'qtyUsed' | 'qtyWasted' | 'qtyReturned', value: number) {
    setLines((current) => current.map((line) =>
      line.itemId === itemId ? { ...line, [field]: Math.max(0, value), dirty: true } : line))
  }

  async function save(draft: boolean) {
    setBusy(true)
    try {
      const result = await request<{ status: string; exceptions: string[]; materialCostCents: number }>(
        `/purchasing/visits/${visitId}/closeout`,
        {
          method: 'POST',
          body: {
            draft,
            lines: lines.map((line) => ({
              itemId: line.itemId,
              yardId: line.yardId,
              qtyUsed: line.qtyUsed,
              qtyWasted: line.qtyWasted,
              qtyReturned: line.qtyReturned,
              note: line.note,
              addedByCrew: line.addedByCrew,
            })),
          },
        })

      if (result.status === 'draft') {
        setNotice('Saved. Come back to it before you clock out.')
      } else if (result.status === 'incomplete') {
        setNotice(`Saved, but it does not add up yet: ${result.exceptions[0]}. Ops can see it.`)
      } else {
        setNotice(`Posted. ${formatCents(result.materialCostCents)} of material on the job.`)
      }
      await load()
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  async function addReceipt() {
    if (!receipt) return
    const cents = Math.round(Number(receipt.amount.replace(/[^0-9.]/g, '')) * 100)
    if (!Number.isFinite(cents) || cents <= 0) {
      setNotice('How much was it?')
      return
    }
    setBusy(true)
    try {
      await request(`/purchasing/visits/${visitId}/field-receipts`, {
        method: 'POST',
        body: {
          vendorName: receipt.vendorName.trim(),
          amountCents: cents,
          description: receipt.description.trim() || null,
        },
      })
      setReceipt(null)
      setNotice('Receipt costed to this job.')
      await load()
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That receipt could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const reconciled = lines.filter(isAccounted).length
  const posted = sheet?.status === 'posted'

  return (
    <div className="flex min-h-app flex-col bg-bg">
      <header className="flex min-h-[60px] shrink-0 flex-wrap items-center gap-3 border-b border-line bg-surface px-5 py-2">
        <span className="text-card font-semibold text-text">{me.tenant.name}</span>
        <Tag>Crew app</Tag>
        <span className="text-body text-text">Close out materials</span>
        <span className="ml-auto text-body text-text-muted">
          Step 2 of 3 · photos → materials → sign-off
        </span>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto p-5">
        {error && (
          <EmptyState title="Close-out could not load" body={error}
            action={{ label: 'Try again', onClick: () => void load() }} />
        )}

        {sheet && lines.length === 0 && !error && (
          <EmptyState
            title="Nothing was loaded for this stop"
            body="A close-out lists what left the yard on the truck. Nothing did, so there is nothing to account for — you can still add a receipt for anything bought in the field."
            action={{ label: 'Add a receipt', onClick: () => setReceipt({ vendorName: '', amount: '', description: '' }) }}
          />
        )}

        {lines.length > 0 && (
          <div className="mx-auto flex max-w-[1100px] flex-col gap-4">
            <Card className="overflow-x-auto p-0">
              <table className="w-full border-collapse text-body">
                <thead>
                  <tr className="border-b border-line text-left">
                    {['Material', 'Loaded', 'Used', 'Wasted', 'Returned', 'Accounted']
                      .map((head) => (
                        <th key={head} className="px-4 py-2.5 text-label text-text-muted">
                          {head.toUpperCase()}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const accounted = line.qtyUsed + line.qtyWasted + line.qtyReturned
                    const ok = isAccounted(line)
                    return (
                      <tr key={line.itemId} className="border-b border-line-soft last:border-b-0">
                        <td className="px-4 py-3">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="text-body font-bold text-text">{line.itemName}</span>
                            {line.swappedFromName && <Tag>Swapped</Tag>}
                            {line.addedByCrew && <Tag>Added by crew</Tag>}
                          </span>
                          {line.swappedFromName && (
                            <span className="block text-cell text-text-muted">
                              Swapped from {line.swappedFromName} by ops
                            </span>
                          )}
                          {line.note && (
                            <span className="block text-cell text-text-muted">{line.note}</span>
                          )}
                        </td>

                        {/* Read-only: what left the yard is the load sheet's
                            answer, not something the crew can revise. */}
                        <td className="px-4 py-3 text-body tnum text-text-muted">
                          {formatNumber(line.qtyLoaded)} {line.unit}
                        </td>

                        {(['qtyUsed', 'qtyWasted', 'qtyReturned'] as const).map((field) => (
                          <td key={field} className="px-4 py-3">
                            <Stepper
                              touch
                              label={`${line.itemName} ${FIELD_LABEL[field]}`}
                              value={line[field]}
                              min={0}
                              max={99_999}
                              onChange={(value) => update(line.itemId, field, value)}
                            />
                          </td>
                        ))}

                        <td className={cn('px-4 py-3 text-body font-bold tnum',
                          ok ? 'text-text' : 'text-warn')}>
                          {line.qtyLoaded > 0
                            ? `${formatNumber(accounted)} of ${formatNumber(line.qtyLoaded)}`
                            : `${formatNumber(line.qtyUsed)} used`}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </Card>

            <Card className="flex flex-wrap items-start gap-4 p-4">
              <div className="min-w-[240px] flex-1">
                <p className="m-0 text-body text-text">
                  Bought something in the field? Photo of the receipt, vendor, amount.
                  Costed to this job and sent to bookkeeping.
                </p>
              </div>
              <Button onClick={() => setReceipt({ vendorName: '', amount: '', description: '' })}>
                Add receipt
              </Button>
            </Card>

            {sheet && sheet.fieldReceipts.length > 0 && (
              <Card className="p-4">
                <SectionLabel className="mb-2 block">Receipts on this stop</SectionLabel>
                <ul className="m-0 list-none space-y-1.5 p-0">
                  {sheet.fieldReceipts.map((row) => (
                    <li key={row.id} className="flex flex-wrap items-baseline gap-2">
                      <span className="text-body text-text">
                        {row.vendorName} · {formatCents(row.amountCents)}
                        {row.description && <> · {row.description}</>}
                      </span>
                      <Tag tone="ok">
                        Costed to this job
                      </Tag>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* §A.1, in the crew's words, at the moment it matters. */}
            <div className="rounded-card border border-accent-line bg-accent-soft p-4">
              <p className="m-0 text-body text-accent-dark">
                This is the moment cost hits the job. Used and wasted are costed at the
                yard’s unit cost; returned goes back on the shelf and costs nothing,
                because it never left. Ops sees any line where accounted ≠ loaded.
              </p>
            </div>
          </div>
        )}
      </main>

      {lines.length > 0 && (
        <footer className="sticky bottom-0 shrink-0 border-t border-line bg-surface px-5 py-3">
          <div className="mx-auto flex max-w-[1100px] flex-wrap items-center gap-3">
            <span className={cn('text-body', reconciled === lines.length ? 'text-text-muted' : 'text-warn')}>
              {reconciled} of {lines.length} lines fully accounted
              {reconciled < lines.length && (() => {
                const bad = lines.find((line) => !isAccounted(line))!
                return ` · ${bad.itemName} reads ${formatNumber(
                  bad.qtyUsed + bad.qtyWasted + bad.qtyReturned)} of ${formatNumber(bad.qtyLoaded)}`
              })()}
            </span>
            <span className="ml-auto flex gap-2">
              <Button size="big" disabled={busy || posted} onClick={() => void save(true)}>
                Save for later
              </Button>
              <Button
                size="big" variant="primary" disabled={busy || posted}
                onClick={() => void save(false)}
              >
                {posted ? 'Posted' : 'Confirm materials → sign-off'}
              </Button>
            </span>
          </div>
        </footer>
      )}

      <Modal
        open={receipt !== null}
        onClose={() => setReceipt(null)}
        title="Receipt from the field"
        footer={
          <>
            <Button onClick={() => setReceipt(null)}>Cancel</Button>
            <Button variant="primary" disabled={busy} onClick={() => void addReceipt()}>
              Cost it to this job
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field
            label="Where from" required autoFocus
            value={receipt?.vendorName ?? ''}
            onChange={(value) => setReceipt((current) => ({ ...current!, vendorName: value }))}
          />
          <Field
            label="Amount" required
            value={receipt?.amount ?? ''}
            onChange={(value) => setReceipt((current) => ({ ...current!, amount: value }))}
            placeholder="38.72"
          />
          <Field
            label="What was it"
            value={receipt?.description ?? ''}
            onChange={(value) => setReceipt((current) => ({ ...current!, description: value }))}
            placeholder="Diamond blade"
          />
          <p className="m-0 text-sub text-text-faint">
            It never reached a yard, so it does not change anybody’s stock — it goes
            straight onto this job.
          </p>
        </div>
      </Modal>

      {notice && (
        <div className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="sr-only"
      >
        Back
      </button>
    </div>
  )
}

const FIELD_LABEL = {
  qtyUsed: 'used', qtyWasted: 'wasted', qtyReturned: 'returned',
} as const

/**
 * A line adds up when used + wasted + returned equals what was loaded. A line
 * the crew added was never loaded, so anything they used is accounted for.
 */
function isAccounted(line: CloseoutLine): boolean {
  const accounted = line.qtyUsed + line.qtyWasted + line.qtyReturned
  if (line.qtyLoaded === 0) return accounted > 0
  return Math.round(accounted * 1000) === Math.round(line.qtyLoaded * 1000)
}
